import {useEffect, useState} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {reaction} from 'mobx';
import {type ICategory} from '@netx/core-category-next/schema';
import {categoriesStore} from '../store';
import {useCategories} from './useCategories';

export type UseCategoryArguments = {
	id?: number;
};

export const useCategory = ({id}: UseCategoryArguments) => {
	const categories = useCategories({ignoreFetch: true});
	const [category, setCategory] = useState<ICategory>();
	const [loading, setLoading] = useState(id != null);

	useEffect(() => {
		if (!id) {
			setLoading(false);
			setCategory(undefined);
			return;
		}

		const dispose = reaction(
			() => categoriesStore.data[id],
			(category, oldCategory, r) => {
				if (category == null || category.categoryid == null) {
					setCategory(undefined);
					return;
				}
				setCategory({...category});
			},
		);

		const category = categoriesStore.get(id);
		if (category) {
			unstable_batchedUpdates(() => {
				setCategory(category);
				setLoading(false);
			});
		} else {
			setLoading(true);
			categories
				.fetch(id)
				.catch(() => setCategory(undefined))
				.finally(() => setLoading(false));
		}

		return () => {
			dispose();
		};
	}, [id]);

	return {
		category,
		loading,
	};
};

export default useCategory;
